// Kırılım uçlarının gövde/sonuç şemaları (spec §6.3 BreakdownGroupModal, §6.4 "Kırılıma
// dönüştür", R10 metaveri önizlemesi). Uçlar: routes/kpi-breakdown.ts (backend, kpi.manage).

import { z } from "zod";
import { KPI_BREAKDOWN_ROLLUPS } from "./kpi.js";

export const KPI_BREAKDOWN_CONVERT_PATTERNS = ["category", "siblings"] as const;
export type KpiBreakdownConvertPattern = (typeof KPI_BREAKDOWN_CONVERT_PATTERNS)[number];

const Label = z.string().trim().min(1).max(200);
const Dimension = z.string().trim().min(1).max(40);

/**
 * `POST /kpi/metrics/:id/breakdown/convert`. Kalıp (a) `category`: `:id` kategori; `targetMetricId`
 * yoksa "yeni toplam" (kategori satırının kendisi toplam olur), varsa kardeş toplam. Kalıp (b)
 * `siblings`: toplam = `targetMetricId ?? :id`, `siblingIds` kardeşleri üye olur. `memberLabels`
 * önizlemedeki kısa etiket önerisini (ya da düzenlenmiş hâlini) uygular.
 */
export const KpiBreakdownConvertBodySchema = z.object({
  pattern: z.enum(KPI_BREAKDOWN_CONVERT_PATTERNS),
  targetMetricId: z.string().uuid().nullable().optional(),
  siblingIds: z.array(z.string().uuid()).max(200).optional(),
  dimension: Dimension,
  /** yalnız hazır listede olmayan (özel) boyutta — sözlükte görünen ad (eski istemci göndermez) */
  dimensionLabel: z.string().trim().min(1).max(80).optional(),
  groupLabel: Label,
  complete: z.boolean().optional(),
  rollup: z.enum(KPI_BREAKDOWN_ROLLUPS).optional(),
  memberLabels: z.record(z.string().uuid(), Label).optional(),
  dryRun: z.boolean().optional(),
});
export type KpiBreakdownConvertBody = z.infer<typeof KpiBreakdownConvertBodySchema>;

export interface KpiBreakdownTreeNode {
  code: string;
  label: string;
  children: KpiBreakdownTreeNode[];
}

export interface KpiBreakdownUntouchedFormula {
  /** null = katalog varsayılan formülü */
  setId: string | null;
  code: string;
  expression: string;
}

export interface KpiBreakdownConvertMember {
  metricId: string;
  code: string;
  label: string;
  /** üst etiket öneki soyulmuş öneri ("Tam Zamanlı - Kadın" → "Kadın") */
  suggestedLabel: string;
  dimensionMember: string;
}

/** Önizleme ve uygulama aynı sayıları döner; `noop` = aynı dönüşüm zaten yapılmış (sayılar 0). */
export interface KpiBreakdownConvertResult {
  dryRun: boolean;
  noop: boolean;
  totalMetricId: string;
  groupId: string | null;
  groupCode: string;
  /** dokunulan set sayısı (tüm müşteriler) */
  sets: number;
  /** yeniden yazılan formül (katalog + set satırı) */
  formulas: number;
  /** raporlanan değere dönüşen (ön izlemede: dönüşecek) ham toplam değeri */
  values: number;
  clients: number;
  /** kalıp (a) kardeş hedef: kategori satırı silindi mi */
  categoryDeleted: boolean;
  members: KpiBreakdownConvertMember[];
  untouchedFormulas: KpiBreakdownUntouchedFormula[];
  /** Üye adaylarından türetilmiş olanların kimlikleri (katalog bayrağı ∪ müşteri effectiveDerived). */
  derivedMembers?: string[];
  tree: { before: KpiBreakdownTreeNode; after: KpiBreakdownTreeNode };
}

const MemberInput = z.union([
  z.object({ metricId: z.string().uuid() }),
  z.object({ label: Label, dimensionMember: z.string().trim().min(1).max(64).optional() }),
]);

/**
 * `POST /kpi/metrics/:id/breakdown/groups` (BreakdownGroupModal kaydı, tek tx): `:id` toplam
 * göstergesi; `groupId` verilirse mevcut gruba üye eklenir, yoksa `dimension` ile yeni grup;
 * `setId` verilirse grup + üyeler o sete (toplamın set satırı altına) eklenir.
 */
export const KpiBreakdownGroupCreateBodySchema = z
  .object({
    setId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    dimension: Dimension.optional(),
    /** yalnız hazır listede olmayan (özel) boyutta — sözlükte görünen ad */
    dimensionLabel: z.string().trim().min(1).max(80).optional(),
    label: Label.optional(),
    complete: z.boolean().optional(),
    rollup: z.enum(KPI_BREAKDOWN_ROLLUPS).optional(),
    /** müşteriye özel grup/yeni üyeler (yalnız yeni grupta) */
    clientId: z.string().uuid().nullable().optional(),
    members: z.array(MemberInput).min(1).max(50),
    dryRun: z.boolean().optional(),
  })
  .refine((b) => b.groupId !== undefined || b.dimension !== undefined, {
    message: "Yeni grup için boyut gerekli",
    path: ["dimension"],
  });
export type KpiBreakdownGroupCreateBody = z.infer<typeof KpiBreakdownGroupCreateBodySchema>;

export interface KpiBreakdownGroupCreateResult {
  dryRun: boolean;
  groupId: string;
  groupCode: string;
  groupCreated: boolean;
  memberIds: string[];
  /** sete eklenen satır sayısı (grup + üyeler) */
  setRows: number;
  /** "Bu müşteride N toplam değeri raporlanan olarak korunacak" */
  values: number;
  clients: number;
}

/** `GET /kpi/metrics/:id/breakdown/preview-metadata` (grup metaverisi yaması önizlemesi, R10). */
export const KpiBreakdownMetadataPreviewQuerySchema = z.object({
  complete: z.enum(["true", "false"]).transform((v) => v === "true"),
  rollup: z.enum(KPI_BREAKDOWN_ROLLUPS),
  dimension: Dimension.optional(),
});
